import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Modal, Toast, Form } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { FaTrash, FaPlus, FaEdit } from 'react-icons/fa';
import { getActiveEvents, deleteEvent, updateEvent } from '../../services/eventService';
import moment from 'moment';
import 'moment/locale/tr';

moment.locale('tr');

const EventList = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [editEvent, setEditEvent] = useState({
    guid: '',
    header: '',
    detail: '',
    date: '',
    location: '',
    mapLocationCode: '',
    picture: null,
    file: null,
    pictureBase64: '',
    fileBase64: '',
    pictureUrl: '',
    fileUrl: ''
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await getActiveEvents();
      console.log('API Response:', response); // Debug log
      if (response?.isSuccess) {
        setEvents(response.data.items || []);
        setError(null);
      } else {
        setError(response?.resultMessage || 'Etkinlikler yüklenirken bir hata oluştu.');
        setEvents([]);
      }
    } catch (err) {
      console.error('Fetch error:', err); // Debug log
      setError('Etkinlikler yüklenirken bir hata oluştu.');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await deleteEvent(selectedEvent.guid);
      if (response.isSuccess) {
        setToastMessage('Etkinlik başarıyla silindi!');
        setToastVariant('success');
        setShowToast(true);
        setEvents(events.filter(event => event.guid !== selectedEvent.guid));
      } else {
        const errorMessage = response.resultMessage || 'Etkinlik silinirken bir hata oluştu.';
        setToastMessage(errorMessage);
        setToastVariant('danger');
        setShowToast(true);
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      const errorMessage = error.response?.data?.message || 'Etkinlik silinirken bir hata oluştu.';
      setToastMessage(errorMessage);
      setToastVariant('danger');
      setShowToast(true);
    } finally {
      setShowDeleteModal(false);
      setSelectedEvent(null);
    }
  };

  const confirmDelete = (event) => {
    setSelectedEvent(event);
    setShowDeleteModal(true);
  };

  const handleEdit = (event) => {
    setSelectedEvent(event);
    setEditEvent({
      guid: event.guid,
      header: event.header,
      detail: event.detail,
      date: event.date,
      location: event.location || '',
      mapLocationCode: event.mapLocationCode || '',
      picture: null,
      file: null,
      pictureBase64: '',
      fileBase64: '',
      pictureUrl: event.pictureUrl || '',
      fileUrl: event.fileUrl || ''
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Mevcut etkinlik verilerini kullanarak güncelleme verilerini oluştur
      const updateData = { ...editEvent };
      
      // Eğer yeni bir resim seçilmemişse ve mevcut resim varsa, mevcut resmi kullan
      if (!updateData.picture && selectedEvent?.pictureUrl) {
        // Mevcut resim bilgisini koru
        updateData.existingPictureUrl = selectedEvent.pictureUrl;
        console.log('Mevcut resim korunuyor:', selectedEvent.pictureUrl);
      } else if (!updateData.picture && !selectedEvent?.pictureUrl) {
        // Eğer resim seçilmemiş ve mevcut resim de yoksa, boş gönder
        updateData.picture = null;
        updateData.pictureBase64 = '';
        updateData.existingPictureUrl = '';
        console.log('Resim seçilmedi ve mevcut resim yok, boş gönderiliyor');
      }
      
      // Eğer yeni bir dosya seçilmemişse ve mevcut dosya varsa, mevcut dosyayı kullan
      if (!updateData.file && selectedEvent?.fileUrl) {
        // Mevcut dosya bilgisini koru
        updateData.existingFileUrl = selectedEvent.fileUrl;
        console.log('Mevcut dosya korunuyor:', selectedEvent.fileUrl);
      } else if (!updateData.file && !selectedEvent?.fileUrl) {
        // Eğer dosya seçilmemiş ve mevcut dosya da yoksa, boş gönder
        updateData.file = null;
        updateData.fileBase64 = '';
        updateData.existingFileUrl = '';
        console.log('Dosya seçilmedi ve mevcut dosya yok, boş gönderiliyor');
      }
      
      console.log('Güncelleme için gönderilen veri:', {
        ...updateData,
        picture: updateData.picture ? 'Yeni resim seçildi' : 'Resim seçilmedi',
        file: updateData.file ? 'Yeni dosya seçildi' : 'Dosya seçilmedi',
        existingPictureUrl: updateData.existingPictureUrl || 'Yok',
        existingFileUrl: updateData.existingFileUrl || 'Yok'
      });
      
      const response = await updateEvent(updateData);
    
    // Log the full response structure to debug
    console.log('Update response structure:', response);
    
    // Check different possible success response formats
    if (response?.isSuccess || response?.data?.isSuccess || response === true || response?.data === true) {
      setToastMessage('Etkinlik başarıyla güncellendi!');
      setToastVariant('success');
      setShowToast(true);
      await fetchEvents();
      setShowEditModal(false);
      setError(null);
    } else {
      const errorMessage = response?.resultMessage || response?.data?.resultMessage || 'Etkinlik güncellenirken bir hata oluştu.';
      setToastMessage(errorMessage);
      setToastVariant('danger');
      setShowToast(true);
      setError(errorMessage);
    }
    } catch (error) {
      console.error('Error updating event:', error);
      const errorMessage = error.response?.data?.message || 'Etkinlik güncellenirken bir hata oluştu.';
      setToastMessage(errorMessage);
      setToastVariant('danger');
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <div className="text-center mt-5">Yükleniyor...</div>
      </Container>
    );
  }

  return (
    <Container fluid>
      <div
        style={{
          position: 'fixed',
          top: 20,
          right: 20,
          zIndex: 9999
        }}
      >
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={1500}
          autohide
          bg={toastVariant}
        >
          <Toast.Body className="text-white">
            {toastMessage}
          </Toast.Body>
        </Toast>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Etkinlikler</h2>
        <Link to="/events/add">
          <Button variant="primary">
            <FaPlus className="me-2" />
            Yeni Etkinlik Ekle
          </Button>
        </Link>
      </div>

      {error ? (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      ) : events.length === 0 ? (
        <div className="text-center mt-5">
          <p>Henüz etkinlik bulunmuyor.</p>
        </div>
      ) : (
        <Row>
          {events.map((event) => (
            <Col key={event.guid} lg={4} md={6} className="mb-4">
              <Card className="h-100 event-card">
                <div className="event-image-container">
                  <Card.Img 
                    variant="top" 
                    src={event.pictureUrl} 
                    className="event-image"
                    onError={(e) => {
                      e.target.src = '/placeholder.jpg';
                    }}
                  />
                </div>
                <Card.Body>
                  <Card.Title>{event.header}</Card.Title>
                  <div className="event-details">
                    <p className="mb-2">
                      <strong>Tarih:</strong> {moment(event.date).format('DD MMMM YYYY, HH:mm')}
                    </p>
                    <p className="mb-2">
                      <strong>Konum:</strong> {event.location}
                    </p>
                    {event.detail && (
                      <p className="mb-2">
                        <strong>Detay:</strong> {event.detail}
                      </p>
                    )}
                  </div>
                </Card.Body>
                <Card.Footer className="bg-white d-flex justify-content-between">
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={() => handleEdit(event)}
                  >
                    <FaEdit className="me-1" /> Düzenle
                  </Button>
                  <Button 
                    variant="outline-danger" 
                    size="sm"
                    onClick={() => confirmDelete(event)}
                  >
                    <FaTrash className="me-1" /> Sil
                  </Button>
                </Card.Footer>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Etkinlik Silme</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Bu etkinliği silmek istediğinizden emin misiniz?
          <br />
          <strong>{selectedEvent?.header}</strong>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            İptal
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Sil
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Etkinlik Düzenle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleEditSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Başlık</Form.Label>
              <Form.Control
                type="text"
                value={editEvent.header}
                onChange={(e) => setEditEvent(prev => ({ ...prev, header: e.target.value }))}
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Detay</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={editEvent.detail}
                onChange={(e) => setEditEvent(prev => ({ ...prev, detail: e.target.value }))}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Tarih</Form.Label>
              <Form.Control
                type="datetime-local"
                value={moment(editEvent.date).format('YYYY-MM-DDTHH:mm')}
                onChange={(e) => setEditEvent(prev => ({ ...prev, date: new Date(e.target.value).toISOString() }))}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Konum</Form.Label>
              <Form.Control
                type="text"
                value={editEvent.location}
                onChange={(e) => setEditEvent(prev => ({ ...prev, location: e.target.value }))}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Harita Konum Kodu</Form.Label>
              <Form.Control
                type="text"
                value={editEvent.mapLocationCode}
                onChange={(e) => setEditEvent(prev => ({ ...prev, mapLocationCode: e.target.value }))}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Resim</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={(e) => setEditEvent(prev => ({ ...prev, picture: e.target.files[0] }))}
              />
              {selectedEvent?.pictureUrl && (
                <div className="mt-2">
                  <small>Mevcut resim: {selectedEvent.pictureUrl}</small>
                </div>
              )}
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Dosya</Form.Label>
              <Form.Control
                type="file"
                onChange={(e) => setEditEvent(prev => ({ ...prev, file: e.target.files[0] }))}
              />
              {selectedEvent?.fileUrl && (
                <div className="mt-2">
                  <small>Mevcut dosya: {selectedEvent.fileUrl}</small>
                </div>
              )}
            </Form.Group>
            
            <div className="d-flex justify-content-end">
              <Button variant="secondary" className="me-2" onClick={() => setShowEditModal(false)}>
                İptal
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? 'Güncelleniyor...' : 'Güncelle'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default EventList;
